// 只保存侧别，不在 URL 放 token 或账号信息。
export const SIDE_QUERY = '__cmhub_side';
export const appSides = Object.freeze({
  microAppCustomerSaas: ['customer', 'merchant'],
  microAppAdmin: ['customer', 'merchant'],
  microAppPublic: ['customer', 'merchant'],
  microAppProcess: ['customer', 'merchant'],
  microAppPublicAuthority: ['customer', 'merchant'],
  microAppScmCapital: ['merchant'],
  microAppFinancial: ['merchant'],
  microAppDeviceCenter: ['merchant'],
  microAppCrm: ['merchant'],
  microAppScmDeliver: ['merchant'],
  microAppScmService: ['merchant'],
  microAppScmMainLogistics: ['merchant'],
  microAppDelivery: ['merchant'],
  microAppGoodManager: ['merchant'],
  microAppPublicMetadata: ['merchant'],
});

// 基座统一开放已登记应用的允许侧别，子应用适配无需重复修改开放名单。
// 此配置仅表示允许加载，不代表对应子应用已完成融合适配或验收。
export const readySides = appSides;
export const instanceKey = (name, side) => `${name}__cmhub_${side}`;
export const routeApp = (route, apps) => apps.find(app => route.path.startsWith(app.activeRule));
export function validateSide(name, side) {
  if (!appSides[name] || appSides[name].indexOf(side) < 0) throw new Error(`微应用客商归属无效：${name}`);
  return side;
}
export function withSide(router, target, side) {
  if (side !== 'customer' && side !== 'merchant') throw new Error('缺少有效的客商侧别');
  if (typeof target === 'string' && (!target.startsWith('/') || target.startsWith('//'))) {
    throw new Error('融合导航只允许站内绝对路径');
  }
  const { route } = router.resolve(target);
  if (!route.path.startsWith('/') || route.path.startsWith('//')) throw new Error('融合导航地址无效');
  return { path: route.path, query: { ...route.query, [SIDE_QUERY]: side }, hash: route.hash };
}
export function resolveRouteSide(to, from, menus, apps, router) {
  const app = routeApp(to, apps);
  if (!app) return null;
  const explicit = to.query[SIDE_QUERY];
  if (explicit !== undefined) return validateSide(app.name, explicit);
  // 同应用详情/按钮导航兼容已接入的旧客应用。跨应用必须由实例导航桥传侧别。
  if (from && routeApp(from, apps) === app && from.query[SIDE_QUERY]) {
    return validateSide(app.name, from.query[SIDE_QUERY]);
  }
  const matches = new Set();
  const visit = nodes => nodes.forEach(node => {
    if (node.menuPath && node.menuPath.startsWith('/') && !node.menuPath.startsWith('//')) {
      const r = router.resolve(node.menuPath).route;
      if (r.path === to.path && Object.keys(r.query).every(k => String(r.query[k]) === String(to.query[k]))) matches.add(node.authSide);
    }
    visit(node.children || []);
  });
  visit(menus);
  if (matches.size === 1) return validateSide(app.name, Array.from(matches)[0]);
  const allowed = appSides[app.name] || [];
  if (!matches.size && allowed.length === 1) return allowed[0];
  throw new Error('该页面无法确定客商归属，请从对应的客/商菜单进入');
}

/** 每个实例专属导航：旧异步回调也只能使用创建时的侧别。 */
export function createNavigation(router, app, side, apps, isActive = () => true) {
  const listeners = new Set();
  let disposed = false;
  const localRoute = () => {
    const r = router.currentRoute;
    if (disposed || !isActive() || !r.path.startsWith(app.activeRule) || r.query[SIDE_QUERY] !== side) return null;
    const query = { ...r.query }; delete query[SIDE_QUERY];
    return { path: `/${r.path.slice(app.activeRule.length)}`, query, hash: r.hash };
  };
  const stop = router.afterEach(() => {
    const r = localRoute();
    if (r) listeners.forEach(fn => fn(r));
  });
  const target = value => {
    if (disposed || !isActive()) throw new Error('融合应用身份已失效');
    const result = withSide(router, value, side);
    const destination = routeApp(result, apps);
    if (destination) validateSide(destination.name, side);
    return result;
  };
  return Object.freeze({
    mode: 'memory', side, getRoute: localRoute,
    push: value => router.push(target(value)),
    replace: value => router.replace(target(value)),
    resolve: value => router.resolve(target(value)),
    subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn); },
    dispose: () => { disposed = true; stop(); listeners.clear(); },
  });
}
